'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import { ClipboardCheck, Eye, ShieldOff } from 'lucide-react';
import { Link } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { getSortingStateParser } from '@/lib/parsers';
import type { ExtendedColumnSort } from '@/types/data-table';
import { Button } from '@/components/ui/button';
import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { useAppSettings } from '@/features/configurations';
import { ocrQaQueries } from '../services/ocr-qa.queries';
import type {
  OcrQaContainerListItem,
  OcrQaListParams,
  OcrQaTier
} from '../types';
import { OcrQaContainerCard } from './ocr-qa-container-card';
import { OcrQaContainerCompactRow } from './ocr-qa-container-compact-row';
import { OcrQaListStatusBadge } from './ocr-qa-list-status-badge';

interface Props {
  tier: OcrQaTier;
}

const TIER_DETAIL_ROUTE: Record<OcrQaTier, string> = {
  1: '/dashboard/qa-ocr-1',
  2: '/dashboard/qa-ocr-2',
  3: '/dashboard/qa-ocr-3'
};

/**
 * Column id → backend `sort_by` key. The columns carry `column*` ids (the
 * per-column permission convention), so a header click would otherwise send an
 * id the API rejects and silently fall back to the default sort. The grid/compact
 * SortControl already emits backend keys, so unmapped ids pass through as-is.
 */
const SORT_KEY_BY_COLUMN: Record<
  string,
  NonNullable<OcrQaListParams['sort_by']>
> = {
  columnContainer: 'container_number',
  columnPagesPending: 'pending_count',
  columnTotalPages: 'total_pages',
  columnOldestPending: 'oldest_pending_at'
};

/** Newest-added container first — see `OcrQaService::list()`. */
const DEFAULT_SORT: ExtendedColumnSort<OcrQaContainerListItem>[] = [
  { id: 'created_at', desc: true }
];

export function OcrQaListPage({ tier }: Props) {
  const t = useTranslations('ocrQa');
  const settings = useAppSettings();
  const [view, setView] = useDataView(`ocr-qa-tier-${tier}`);

  const permissionGroup = `qa${tier}_ocr` as const;

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(20));
  const [urlContainer] = useQueryState(
    'columnContainer',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlPagesPending] = useQueryState(
    'columnPagesPending',
    parseAsString.withDefault('')
  );
  const [urlTotalPages] = useQueryState(
    'columnTotalPages',
    parseAsString.withDefault('')
  );
  const [urlOldestPending] = useQueryState(
    'columnOldestPending',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  // List membership filter, driven by the `listStatus` column filter so it
  // renders as a chip alongside the other toolbar filters. 'active' (the
  // default, and what an empty selection means) hides completed containers,
  // matching the backend; 'all' includes everything; 'completed' narrows to
  // completed. Only sent to the API when it deviates from the default.
  const [urlStatus] = useQueryState(
    'listStatus',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<OcrQaContainerListItem>().withDefault(DEFAULT_SORT)
  );

  const queryParams = useMemo<OcrQaListParams>(() => {
    const sortId = urlSorting[0]?.id as string | undefined;
    const sortBy = sortId
      ? (SORT_KEY_BY_COLUMN[sortId] ??
        (sortId as NonNullable<OcrQaListParams['sort_by']>))
      : 'created_at';
    const sortDir: 'asc' | 'desc' = urlSorting[0]?.desc ? 'desc' : 'asc';

    const params: OcrQaListParams = {
      page: urlPage,
      per_page: urlPerPage,
      sort_by: sortBy,
      sort_direction: sortDir
    };

    if (urlContainer.length) {
      params.container_number = urlContainer.join(',');
    }
    if (urlPagesPending) {
      const parts = String(urlPagesPending)
        .split(',')
        .map(Number)
        .filter((n) => !isNaN(n));
      if (parts.length >= 1) params.min_pending = parts[0];
      if (parts.length >= 2) params.max_pending = parts[1];
    }
    if (urlTotalPages) {
      const parts = String(urlTotalPages)
        .split(',')
        .map(Number)
        .filter((n) => !isNaN(n));
      if (parts.length >= 1) params.min_pages = parts[0];
      if (parts.length >= 2) params.max_pages = parts[1];
    }
    if (urlOldestPending.length) {
      const t0 = Number(urlOldestPending[0]);
      const t1 = Number(urlOldestPending[1]);
      if (t0)
        params.oldest_pending_from = new Date(t0).toISOString().split('T')[0];
      if (t1)
        params.oldest_pending_to = new Date(t1).toISOString().split('T')[0];
    }
    const status = urlStatus[0];
    if (status === 'all' || status === 'completed') {
      params.status = status;
    }

    return params;
  }, [
    urlPage,
    urlPerPage,
    urlContainer,
    urlPagesPending,
    urlTotalPages,
    urlOldestPending,
    urlStatus,
    urlSorting
  ]);

  const showQa2DisabledBanner = tier === 2 && !settings.enable_qa2_ocr;

  const { data: listResponse, isPending } = useQuery(
    ocrQaQueries.list(tier, queryParams)
  );

  const items = useMemo(
    () => (listResponse?.data ?? []) as OcrQaContainerListItem[],
    [listResponse]
  );
  const pagination = listResponse?.pagination;
  const pageCount = pagination?.totalPages ?? 0;

  const columns = useMemo<ColumnDef<OcrQaContainerListItem>[]>(
    () => [
      {
        id: 'columnContainer',
        accessorKey: 'container_number',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('columnContainer')} />
        ),
        cell: ({ row, column }) => (
          <span className='font-medium'>
            <HighlightMatch
              text={row.original.container_number}
              query={getColumnSearchTerms(column)}
            />
          </span>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('columnContainer'),
          variant: 'text' as const,
          placeholder: t('searchPlaceholder'),
          permission: `${permissionGroup}.column.columnContainer`
        }
      },
      {
        id: 'columnPagesPending',
        accessorKey: 'pending_count',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('columnPagesPending')}
          />
        ),
        cell: ({ row }) => (
          <span className='tabular-nums'>{row.original.pending_count}</span>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
          label: t('columnPagesPending'),
          variant: 'range' as const,
          range: [0, 200] as [number, number],
          permission: `${permissionGroup}.column.columnPagesPending`
        }
      },
      {
        id: 'columnPagesInProgress',
        accessorKey: 'in_progress_count',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('columnPagesInProgress')}
          />
        ),
        cell: ({ row }) => (
          <span className='tabular-nums'>{row.original.in_progress_count}</span>
        ),
        enableSorting: false,
        meta: {
          label: t('columnPagesInProgress'),
          permission: `${permissionGroup}.column.columnPagesInProgress`
        }
      },
      {
        id: 'columnTotalPages',
        accessorKey: 'total_pages',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('columnTotalPages')}
          />
        ),
        cell: ({ row }) => (
          <span className='tabular-nums'>{row.original.total_pages}</span>
        ),
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
          label: t('columnTotalPages'),
          variant: 'range' as const,
          range: [0, 500] as [number, number],
          permission: `${permissionGroup}.column.columnTotalPages`
        }
      },
      {
        id: 'columnOldestPending',
        accessorKey: 'oldest_pending_at',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('columnOldestPending')}
          />
        ),
        cell: ({ row }) =>
          row.original.oldest_pending_at ? (
            <span className='text-muted-foreground text-xs'>
              {new Date(row.original.oldest_pending_at).toLocaleString()}
            </span>
          ) : (
            <span className='text-muted-foreground'>—</span>
          ),
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
          label: t('columnOldestPending'),
          variant: 'dateRange' as const,
          permission: `${permissionGroup}.column.columnOldestPending`
        }
      },
      {
        // Derived lifecycle status — always visible (no column permission
        // gate). Its filter is list membership (active / all / completed),
        // rendered as a toolbar chip like every other filter.
        id: 'listStatus',
        accessorKey: 'list_status',
        header: () => <span>{t('columnListStatus')}</span>,
        cell: ({ row }) => (
          <div className='flex items-center gap-1.5'>
            <OcrQaListStatusBadge status={row.original.list_status} />
            {row.original.qa_return_count > 0 && (
              <span
                className='text-muted-foreground text-xs tabular-nums'
                title={t('returnCount', {
                  count: row.original.qa_return_count
                })}
              >
                ×{row.original.qa_return_count}
              </span>
            )}
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
        enableColumnFilter: true,
        meta: {
          label: t('columnListStatus'),
          variant: 'select' as const,
          options: [
            { label: t('statusFilterActive'), value: 'active' },
            { label: t('statusFilterAll'), value: 'all' },
            { label: t('statusFilterCompleted'), value: 'completed' }
          ]
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('columnActions')}</div>,
        cell: ({ row }) => (
          <div className='flex justify-end'>
            <Button asChild size='sm' variant='ghost'>
              <Link
                href={`${TIER_DETAIL_ROUTE[tier]}/${row.original.container_id}`}
              >
                <Eye className='me-1 h-4 w-4' />
                {t('open')}
              </Link>
            </Button>
          </div>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, tier, permissionGroup]
  );

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<OcrQaContainerListItem>({
    columns: filteredColumns,
    pageKey: `ocr-qa-tier-${tier}`,
    data: items,
    pageCount,
    initialState: {
      sorting: DEFAULT_SORT,
      pagination: { pageIndex: 0, pageSize: 20 }
    }
  });

  const sortOptions = useMemo(
    () => [
      { label: t('sortRecentlyAdded'), value: 'created_at' },
      { label: t('columnContainer'), value: 'container_number' },
      { label: t('columnPagesPending'), value: 'pending_count' },
      { label: t('columnTotalPages'), value: 'total_pages' },
      { label: t('columnOldestPending'), value: 'oldest_pending_at' }
    ],
    [t]
  );

  const tableState = table.state;

  const emptyState = (
    <div className='flex flex-col items-center justify-center rounded-lg border py-12 text-center'>
      <ClipboardCheck className='text-muted-foreground mb-4 h-12 w-12' />
      <p className='text-muted-foreground text-sm'>{t('emptyState')}</p>
    </div>
  );

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between gap-4'>
        <div>
          <h1 className='flex items-center gap-2 text-2xl font-bold tracking-tight'>
            <ClipboardCheck className='text-primary h-6 w-6' />
            {t(`tier${tier}Title`)}
          </h1>
          <p className='text-muted-foreground text-sm'>
            {t(`tier${tier}Description`)}
          </p>
        </div>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey={`ocr-qa-tier-${tier}`}
          />
        </div>
      </div>

      {showQa2DisabledBanner ? (
        <div className='flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-amber-700 dark:text-amber-300'>
          <ShieldOff className='h-5 w-5 shrink-0' />
          <p className='text-sm'>{t('qa2DisabledByAdmin')}</p>
        </div>
      ) : null}

      <DataTableToolbar
        table={table}
        viewFilterPermission={`${permissionGroup}.view_filter`}
      />

      {isPending ? (
        <ListingSkeleton view={view} />
      ) : items.length === 0 && tableState.columnFilters.length === 0 ? (
        emptyState
      ) : view === 'grid' ? (
        <>
          <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {items.map((item) => (
              <OcrQaContainerCard
                key={item.container_id}
                item={item}
                tier={tier}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div className='space-y-2'>
            {items.map((item) => (
              <OcrQaContainerCompactRow
                key={item.container_id}
                item={item}
                tier={tier}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div className={cn('flex min-h-[500px] flex-col gap-2')}>
          <DataTable table={table} />
        </div>
      )}
    </div>
  );
}
